<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { quotaState } from '../security/quotaState';

const { t } = useI18n();

const contact = computed(() => quotaState.payContact || '@autospy_group');
const telegramUrl = computed(() => `https://t.me/${contact.value.replace(/^@/, '')}`);
const limit = computed(() => quotaState.limit || 3);
const isNetwork = computed(() => quotaState.scope === 'network');
const title = computed(() => t(isNetwork.value ? 'paywall.networkTitle' : 'paywall.title'));
const body = computed(() => t(isNetwork.value ? 'paywall.networkBody' : 'paywall.body', { limit: limit.value }));

const copied = ref(false);
async function copyDevice(): Promise<void> {
  try {
    await navigator.clipboard.writeText(quotaState.device);
    copied.value = true;
    window.setTimeout(() => (copied.value = false), 1500);
  } catch {
    /* clipboard blocked — the code is still selectable on screen */
  }
}

function retry(): void {
  quotaState.blocked = false;
}
</script>

<template>
  <div v-if="quotaState.blocked" class="paywall" role="dialog" aria-modal="true">
    <div class="card">
      <div class="lock" aria-hidden="true">🔒</div>
      <h2>{{ title }}</h2>
      <p class="body">{{ body }}</p>
      <p class="instruction">{{ t('paywall.instruction', { contact }) }}</p>

      <div class="device">
        <span class="device-label">{{ t('paywall.device') }}</span>
        <button type="button" class="device-code" @click="copyDevice" :title="quotaState.device">
          <code>{{ quotaState.device || '—' }}</code>
          <span class="copy">{{ copied ? '✓' : '⧉' }}</span>
        </button>
      </div>

      <a class="tg" :href="telegramUrl" target="_blank" rel="noopener noreferrer">
        {{ t('paywall.telegram') }}
      </a>
      <button type="button" class="retry" @click="retry">{{ t('paywall.retry') }}</button>
    </div>
  </div>
</template>

<style scoped>
.paywall {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--overlay);
  backdrop-filter: blur(10px);
}
.card {
  width: 100%;
  max-width: 380px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-float);
  padding: 28px 24px;
  text-align: center;
  color: var(--text);
}
.lock {
  font-size: 34px;
  margin-bottom: 8px;
}
h2 {
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 650;
}
.body {
  margin: 0 0 6px;
  color: var(--text);
}
.instruction {
  margin: 0 0 18px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.45;
}
.device {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 18px;
  text-align: left;
}
.device-label {
  font-size: 12px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.device-code {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  cursor: pointer;
}
.device-code code {
  flex: 1;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  word-break: break-all;
  text-align: left;
}
.copy {
  color: var(--accent);
  font-size: 15px;
}
.tg {
  display: block;
  width: 100%;
  padding: 13px;
  margin-bottom: 10px;
  background: var(--accent);
  color: var(--on-accent);
  border-radius: var(--radius);
  font-weight: 650;
  text-decoration: none;
}
.retry {
  width: 100%;
  padding: 11px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--muted);
  font-size: 14px;
  cursor: pointer;
}
.retry:active {
  background: var(--surface-2);
}
</style>
